

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { OnboardingProgress } from "@/types/OnboardingProgress";
import {
  ActiveStep,
  getActiveSteps,
  ONBOARDING_TRACKS,
} from "@/components/POnboarding/onboarding-steps";
import {
  getOnboarding,
  completeStep as completeStepApi,
  setOnboardingState,
} from "@/lib/api/handlers/OnboardingHandling";
import { isServerErr } from "@/lib/api/errors/ErrorHandler";
import { tokenStore } from "@/lib/auth/tokenStore";
import { ACCOUNT_TEARDOWN_EVENT } from "@/lib/storage/accountScopedState";

/**
 * "Minimized" is the SERVER's (per user + company — `OnboardingService`), this
 * key only paints the first frame before the GET answers. Company-scoped: a
 * global key carried one company's choice into the next. The old global
 * `veltric-onboarding-minimized` is neither read nor migrated (PB-FB-N15072);
 * `clearAccountScopedState` sweeps both on sign-out.
 */
const MINIMIZED_KEY_PREFIX = "veltric-onboarding-minimized:";

function minimizedKeyOf(companyId: string | null): string | null {
  return companyId ? `${MINIMIZED_KEY_PREFIX}${companyId}` : null;
}

function readCompanyId(): string | null {
  try {
    return tokenStore.getCompanyId();
  } catch {
    return null; // storage blocked
  }
}

function readUserId(): string | null {
  try {
    return tokenStore.getUserId();
  } catch {
    return null; // storage blocked
  }
}

function readStoredMinimized(key: string | null): boolean {
  if (!key) return false;
  try {
    return localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
}

function writeStoredMinimized(key: string | null, value: boolean): void {
  if (!key) return;
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // localStorage unavailable
  }
}

/**
 * How long an unanswered minimize PUT holds GETs back. axios sets no timeout:
 * a PUT that never answered froze the sync for the rest of the page load — no
 * GET was applied again, another device's change never arrived (audit r2 L2).
 * Past this the PUT is presumed lost and the server is the truth again — and
 * if it answers after all, it is re-applied (see `toggleMinimized`).
 *
 * Measured on the monotonic clock (`performance.now()`): the wall clock jumps
 * — a laptop waking up, an NTP correction, a hand-set date — and a jump aged
 * a PUT that had just left (audit r3 R3-5).
 */
const PUT_HOLDS_GETS_MS = 30_000;

/**
 * The minimize bookkeeping of the whole page load, not of one mount: every
 * shell (dashboard, event, user area, …) mounts its OWN provider, so a shell
 * switch unmounts the provider whose minimize PUT is still out and mounts a
 * fresh one — whose first GET can read the server before that PUT is written.
 * Per-mount refs could not see the other mount's PUT; the stale
 * `minimized:false` re-opened the guide and wrote "false" into the key
 * (audit r1 M2).
 *
 * The page load is not the account's, though: a sign-out does not reload the
 * tab (`AuthContext.logout`), so the next user — same company — inherited the
 * last one's in-flight PUTs (holding their GETs back, rolling them back on a
 * refusal) and confirmed value (audit r2 L3). The account sweep's event ends
 * it all ({@link endAccountMinimizeSync}), and `confirmed` carries the user AND
 * company it was confirmed for (a company switch is a full reload anyway).
 */
const minimizeSync = {
  /** Minimize PUTs not answered yet — from any mount — by id: when each started (`performance.now()`). */
  putsInFlight: new Map<number, number>(),
  nextPutId: 0,
  /** Bumped by every toggle — from any mount. */
  generation: 0,
  /** Bumped by every sign-out: a GET or PUT from before it is the last account's. */
  epoch: 0,
  /**
   * The last value the server confirmed — an applied GET or an accepted PUT —
   * with the toggle generation it belongs to. A PUT answer only replaces a
   * confirmation from an older generation: an older click's PUT answering
   * after a newer one's must not become the rollback value (audit r2 L4).
   */
  confirmed: null as { scope: string; value: boolean; generation: number } | null,
  /** The mounted providers — a rollback reaches whichever one is on screen. */
  listeners: new Set<(value: boolean) => void>(),
};

/** A minimize PUT younger than {@link PUT_HOLDS_GETS_MS} is still out. */
function putHoldsGets(): boolean {
  const now = performance.now();
  for (const startedAt of minimizeSync.putsInFlight.values()) {
    if (now - startedAt < PUT_HOLDS_GETS_MS) return true;
  }
  return false;
}

/**
 * Sign-out (or another account signing in): the GETs and PUTs still out are
 * the last account's — the PUTs no longer hold GETs back, and none of them
 * applies, rolls back or records anything when it answers.
 */
function endAccountMinimizeSync(): void {
  minimizeSync.epoch += 1;
  minimizeSync.putsInFlight.clear();
  minimizeSync.confirmed = null;
}

if (typeof window !== "undefined") {
  window.addEventListener(ACCOUNT_TEARDOWN_EVENT, endAccountMinimizeSync);
}

/** Who a confirmation belongs to: onboarding is per user × company. */
function scopeOf(userId: string | null, companyId: string | null): string {
  return `${userId ?? ""}|${companyId ?? ""}`;
}

/**
 * Test-only: the bookkeeping outlives a test's unmount. The PUT id counter is
 * left running (audit r3 R3-8): a previous test's PUT that settles late runs
 * its `.finally` delete then — with the ids restarted it would delete the
 * new test's in-flight PUT of the same number.
 */
export function __resetOnboardingMinimizeSyncForTests(): void {
  minimizeSync.putsInFlight.clear();
  minimizeSync.generation = 0;
  minimizeSync.epoch = 0;
  minimizeSync.confirmed = null;
  minimizeSync.listeners.clear();
}

interface OnboardingContextType {
  progress: OnboardingProgress | null;
  activeSteps: ActiveStep[];
  completedCount: number;
  totalCount: number;
  percentComplete: number;
  isMinimized: boolean;
  isDismissed: boolean;
  isAllComplete: boolean;
  justCompletedAll: boolean;
  recentlyCompletedKeys: Set<string>;
  mounted: boolean;
  completeStep: (stepKey: string) => void;
  dismiss: () => void;
  toggleMinimized: () => void;
  refetch: () => void;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(
  undefined,
);

export const OnboardingProvider: React.FC<{
  children: React.ReactNode;
  initialProgress: OnboardingProgress | null;
}> = ({ children, initialProgress }) => {
  const pathname = useLocation().pathname;
  const [progress, setProgress] = useState<OnboardingProgress | null>(
    initialProgress,
  );
  // The company is fixed for the lifetime of a mount: switching companies is a
  // full reload (`window.location.href = "/"`), and every mount sits behind
  // auth — a sign-out unmounts it, the next sign-in mounts a new one.
  const [{ minimizedKey, scope }] = useState(() => {
    const companyId = readCompanyId();
    return { minimizedKey: minimizedKeyOf(companyId), scope: scopeOf(readUserId(), companyId) };
  });
  const [isMinimized, setIsMinimizedState] = useState(
    () => initialProgress?.minimized ?? readStoredMinimized(minimizedKey),
  );
  // Mirrors the state synchronously, so two clicks inside one render each flip
  // the value the other one left.
  const isMinimizedRef = useRef(isMinimized);
  const applyMinimized = useCallback(
    (value: boolean) => {
      isMinimizedRef.current = value;
      setIsMinimizedState(value);
      writeStoredMinimized(minimizedKey, value);
    },
    [minimizedKey],
  );
  // A rolled-back toggle reaches this mount even when another mount sent it.
  useEffect(() => {
    minimizeSync.listeners.add(applyMinimized);
    return () => {
      minimizeSync.listeners.delete(applyMinimized);
    };
  }, [applyMinimized]);
  const [isDismissed, setIsDismissed] = useState(
    initialProgress?.dismissed ?? false,
  );
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Track step keys with pending API calls so refetches preserve optimistic state
  const pendingStepKeys = useRef(new Set<string>());

  // Refetch on window focus to pick up auto-completions
  // A GET must not overwrite a toggle it did not see — from this mount or any
  // other (`minimizeSync`): no minimize PUT in flight at its start or its end,
  // and no toggle in between. A PUT out for longer than 30 s no longer counts.
  const refetch = useCallback(() => {
    const generationAtStart = minimizeSync.generation;
    const epochAtStart = minimizeSync.epoch;
    // A GET the server answered while a PUT was being written may hold the
    // value from before it — even when it lands after the PUT returned.
    const startedWhilePutInFlight = putHoldsGets();
    getOnboarding().then((result) => {
      if (result && !("isServerError" in result)) {
        // Clear pending keys that the server has confirmed
        for (const key of [...pendingStepKeys.current]) {
          if (result.completedSteps?.[key]) {
            pendingStepKeys.current.delete(key);
          }
        }
        // Merge remaining unconfirmed pending steps into server data
        if (pendingStepKeys.current.size > 0) {
          const merged = { ...result.completedSteps };
          for (const key of pendingStepKeys.current) {
            if (!merged[key]) {
              merged[key] = new Date().toISOString();
            }
          }
          setProgress({ ...result, completedSteps: merged });
        } else {
          setProgress(result);
        }
        setIsDismissed(result.dismissed);
        if (
          !startedWhilePutInFlight &&
          !putHoldsGets() &&
          minimizeSync.generation === generationAtStart &&
          minimizeSync.epoch === epochAtStart
        ) {
          const serverValue = result.minimized === true;
          minimizeSync.confirmed = {
            scope,
            value: serverValue,
            generation: minimizeSync.generation,
          };
          applyMinimized(serverValue);
        }
      } else if (result && "isServerError" in result) {
        console.error("[Onboarding] refetch failed:", result.message);
      }
    }).catch((err) => {
      console.error("[Onboarding] refetch network error:", err);
    });
  }, [applyMinimized, scope]);

  // Refetch on navigation to pick up backend auto-completions (e.g. user
  // creates an event → backend marks the step → redirect shows updated
  // progress). Throttled because the audit found real dependencies on
  // pathname-driven refetch for `hasSubscription` / `hasCompanyProfile`
  // (merged into completedSteps below) — explicit `onboarding-refetch`
  // dispatches don't cover those code paths. 60 s is short enough to feel
  // live and long enough that a burst of sidebar clicks collapses to one
  // request.
  const lastNavRefetchAtRef = useRef(0);
  useEffect(() => {
    if (!mounted || pathname?.includes("/auth")) return;
    const now = Date.now();
    if (now - lastNavRefetchAtRef.current < 60_000) return;
    lastNavRefetchAtRef.current = now;
    refetch();
  }, [mounted, pathname, refetch]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refetch();
      }
    };
    const handleOnboardingRefetch = () => refetch();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("onboarding-refetch", handleOnboardingRefetch);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("onboarding-refetch", handleOnboardingRefetch);
    };
  }, [refetch]);

  // Derived state
  // Plugins are event-level, not company-level. Onboarding is company-wide,
  // so we show all steps regardless of plugins by passing all known plugin names.
  const activeSteps = useMemo(() => {
    if (!progress) return [];

    // Merge live DTO fields into completedSteps so completion is authoritative
    // even if the completeStep PUT didn't succeed
    const mergedSteps = { ...progress.completedSteps };
    if (progress.hasSubscription && !mergedSteps["choose_plan"]) {
      mergedSteps["choose_plan"] = new Date().toISOString();
    }
    if (progress.hasCompanyProfile && !mergedSteps["complete_company_profile"]) {
      mergedSteps["complete_company_profile"] = new Date().toISOString();
    }

    return getActiveSteps(
      ONBOARDING_TRACKS,
      progress.activeTracks,
      progress.companyRole,
      ["DASHBOARD", "WEBSITE", "MESSAGING", "SLOTTING", "CHECK_IN", "SEATING", "ACTIONS", "ATTACHMENTS", "DOCUMENTS"],
      mergedSteps,
      progress.customerType,
    );
  }, [progress]);

  const completedCount = useMemo(
    () => activeSteps.filter((s) => s.isCompleted).length,
    [activeSteps],
  );

  const totalCount = activeSteps.length;

  const percentComplete = useMemo(
    () => (totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0),
    [completedCount, totalCount],
  );

  const isAllComplete = totalCount > 0 && percentComplete === 100;

  // Fresh-completion tracking: animate only keys that flipped to complete in
  // this session. seenCompletedRef is seeded with whatever was already done
  // on first mount so reloads / route nav don't replay old completions.
  const seenCompletedRef = useRef<Set<string> | null>(null);
  const [recentlyCompletedKeys, setRecentlyCompletedKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const lastPercentRef = useRef<number | null>(null);
  const [justCompletedAll, setJustCompletedAll] = useState(false);

  useEffect(() => {
    if (!mounted || !progress || seenCompletedRef.current) return;
    const seed = new Set<string>(Object.keys(progress.completedSteps ?? {}));
    if (progress.hasSubscription) seed.add("choose_plan");
    if (progress.hasCompanyProfile) seed.add("complete_company_profile");
    seenCompletedRef.current = seed;
  }, [mounted, progress]);

  useEffect(() => {
    const seen = seenCompletedRef.current;
    if (!seen) return;
    const fresh: string[] = [];
    for (const s of activeSteps) {
      if (s.isCompleted && !seen.has(s.key)) {
        seen.add(s.key);
        fresh.push(s.key);
      }
    }
    if (fresh.length === 0) return;
    setRecentlyCompletedKeys((prev) => {
      const next = new Set(prev);
      for (const k of fresh) next.add(k);
      return next;
    });
    // No cleanup: each batch's timer must outlive subsequent effect re-runs
    // so a second completion within 900ms doesn't cancel the first batch's removal.
    window.setTimeout(() => {
      setRecentlyCompletedKeys((prev) => {
        const next = new Set(prev);
        for (const k of fresh) next.delete(k);
        return next;
      });
    }, 900);
  }, [activeSteps]);

  // Steps whose complete-step PUT already failed this session. Without this
  // guard every marker remount / refetch cycle re-fired the PUT against a
  // 5xx-ing endpoint in an unbounded retry storm (BUGS.md H16, FIXED
  // 2026-07-05 W2-E). Onboarding is best-effort — fail once, stay quiet
  // until the next full page load.
  const failedStepKeys = useRef<Set<string>>(new Set());

  // Actions
  const completeStep = useCallback(
    (stepKey: string) => {
      if (failedStepKeys.current.has(stepKey)) return;
      // Optimistic update
      setProgress((prev) => {
        if (!prev) return prev;
        if (prev.completedSteps?.[stepKey]) return prev;
        return {
          ...prev,
          completedSteps: {
            ...prev.completedSteps,
            [stepKey]: new Date().toISOString(),
          },
        };
      });
      // Track pending step so concurrent refetches preserve this optimistic completion
      pendingStepKeys.current.add(stepKey);
      completeStepApi(stepKey)
        .then((result) => {
          // Revert optimistic update on server error
          if (result && typeof result === "object" && "isServerError" in result) {
            console.error("[Onboarding] completeStep failed for", stepKey, result.message);
            failedStepKeys.current.add(stepKey);
            pendingStepKeys.current.delete(stepKey);
            setProgress((prev) => {
              if (!prev) return prev;
              const rest = { ...prev.completedSteps };
              delete rest[stepKey];
              return { ...prev, completedSteps: rest };
            });
          }
          // On success: don't remove from pendingStepKeys here.
          // The next refetch will confirm persistence and clear it.
        })
        .catch((err) => {
          // Revert optimistic update on network error
          console.error("[Onboarding] completeStep network error for", stepKey, err);
          failedStepKeys.current.add(stepKey);
          pendingStepKeys.current.delete(stepKey);
          setProgress((prev) => {
            if (!prev) return prev;
            const rest = { ...prev.completedSteps };
            delete rest[stepKey];
            return { ...prev, completedSteps: rest };
          });
        });
    },
    [],
  );

  // Leaves `minimized` alone — on the server too (an omitted field is kept).
  const dismiss = useCallback(() => {
    setIsDismissed(true);
    setOnboardingState(true).catch(() => {});
  }, []);

  // Detect <100 → 100 transition this session: show celebration card for ~5s,
  // then dismiss permanently. Skip the very first run after mount so a user
  // who is already at 100% on load doesn't see the celebration replay.
  useEffect(() => {
    if (!mounted || totalCount === 0) return;
    const prev = lastPercentRef.current;
    lastPercentRef.current = percentComplete;
    if (prev === null) return;
    if (prev < 100 && percentComplete === 100) {
      setJustCompletedAll(true);
      const t = setTimeout(() => {
        setJustCompletedAll(false);
        dismiss();
      }, 5000);
      return () => clearTimeout(t);
    }
  }, [mounted, percentComplete, totalCount, dismiss]);

  // Optimistic; a refused PUT (the handler resolves a ServerError, it never
  // rejects) rolls back only if no later toggle has happened since — anywhere —
  // the second of two quick clicks is what the user wants. It rolls back to
  // what the SERVER last confirmed, not to the value before this click: after
  // two refused clicks that click's "before" was never stored. An accepted
  // PUT is that confirmation only if no newer click's PUT was accepted first
  // (audit r2 L4): click 1, click 2, PUT 2 ok, PUT 1 ok ⇒ the server's value
  // is click 2's; click 1, click 2, PUT 1 ok, PUT 2 refused ⇒ click 1's.
  // An accepted PUT that is still the newest toggle is re-applied: out for
  // longer than 30 s it let a GET through, which showed the value from before
  // the click — the click visibly undid itself (audit r3 R3-5).
  const toggleMinimized = useCallback(() => {
    const previous = isMinimizedRef.current;
    const next = !previous;
    const generation = ++minimizeSync.generation;
    const epoch = minimizeSync.epoch;
    applyMinimized(next);
    const putId = ++minimizeSync.nextPutId;
    minimizeSync.putsInFlight.set(putId, performance.now());
    setOnboardingState(undefined, next)
      .then((result) => {
        // Signed out since: the answer is the last account's (audit r2 L3).
        if (epoch !== minimizeSync.epoch) return;
        if (!isServerErr(result)) {
          const confirmed = minimizeSync.confirmed;
          if (!confirmed || confirmed.scope !== scope || generation >= confirmed.generation) {
            minimizeSync.confirmed = { scope, value: next, generation };
          }
          if (minimizeSync.generation === generation) {
            writeStoredMinimized(minimizedKey, next);
            for (const apply of minimizeSync.listeners) apply(next);
          }
          return;
        }
        if (minimizeSync.generation !== generation) return;
        const confirmed = minimizeSync.confirmed;
        const value = confirmed && confirmed.scope === scope ? confirmed.value : previous;
        // This mount may be gone (a shell switch): the key and every mount on
        // screen still learn the rollback.
        writeStoredMinimized(minimizedKey, value);
        for (const apply of minimizeSync.listeners) apply(value);
      })
      .catch(() => {})
      .finally(() => {
        minimizeSync.putsInFlight.delete(putId);
      });
  }, [applyMinimized, minimizedKey, scope]);

  const value = useMemo(
    () => ({
      progress,
      activeSteps,
      completedCount,
      totalCount,
      percentComplete,
      isMinimized,
      isDismissed,
      isAllComplete,
      justCompletedAll,
      recentlyCompletedKeys,
      mounted,
      completeStep,
      dismiss,
      toggleMinimized,
      refetch,
    }),
    [
      progress,
      activeSteps,
      completedCount,
      totalCount,
      percentComplete,
      isMinimized,
      isDismissed,
      isAllComplete,
      justCompletedAll,
      recentlyCompletedKeys,
      mounted,
      completeStep,
      dismiss,
      toggleMinimized,
      refetch,
    ],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error(
      "useOnboarding must be used within an OnboardingProvider",
    );
  }
  return context;
};

/**
 * Provider-optional variant for pages that only OPPORTUNISTICALLY report step
 * completion (e.g. the programme page auto-completing "create_first_offering"
 * on the first bookable-block create). Returns null outside a provider instead
 * of throwing, so such pages stay mountable in provider-less tests/hosts.
 */
export const useOnboardingOptional = () => useContext(OnboardingContext) ?? null;
